/**
 * @fileoverview Table detail slide-in panel for the Schema Manifest Editor grid.
 * @module system-web/components/migration/TableDetailSlideIn
 *
 * Responsibilities:
 * - Render four-tab detail view for a selected schema manifest table entry.
 * - Provide an Enable / Disable action button in the panel header area.
 * - Display General metadata, Columns with PK/FK markers, Foreign Keys (raw constraints),
 *   and Relationships (bidirectional: parent-side + child-side).
 *
 * Dependencies:
 * - lucide-react for icons.
 * - SchemaManifestTableEntry, SchemaManifestFk types from legacySchemaManifestService.
 *
 * Architecture:
 * - Local-first: all data supplied by parent via props from useLegacySchemaManifest.
 * - No direct API calls; no direct Dexie access.
 * - Exported as a standalone component so it can be used as GridPageShell panelContent
 *   with a separately-supplied panelHeader.
 *
 * Notes:
 * - The Enable/Disable button is rendered via the exported TableDetailSlideInHeader component
 *   and wired into GridPageShell's `panelHeader` prop.
 * - Tabs: General | Columns | Foreign Keys | Relationships
 */

import React, { useState } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import type { SchemaManifestFk, SchemaManifestTableEntry } from '../../services/legacySchemaManifestService';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract TableDetailSlideInProps
 *
 * Purpose: Props for the four-tab table detail slide-in body.
 *
 * Fields:
 * - table: The selected manifest table entry (all tabs read from this).
 * - fks: Full pendingFks list; the component filters to this table.
 * - onRemoveFk: Called with the global FK index when the admin removes a FK from the
 *               Foreign Keys tab.
 * - onToggleColumnDisabled: Called when the admin disables or re-enables a column from
 *   the Columns tab.
 *
 * Rules:
 * - table must be a value from pendingTables (never null when rendered).
 * - No API calls from this component.
 *
 * Sync:
 * - All data sourced from useLegacySchemaManifest via parent component props.
 */
export interface TableDetailSlideInProps {
  table: SchemaManifestTableEntry;
  fks: SchemaManifestFk[];
  onRemoveFk: (index: number) => void;
  onToggleColumnDisabled: (tableName: string, columnName: string) => void;
}

/**
 * @contract TableDetailSlideInHeaderProps
 *
 * Purpose: Props for the slide-in panel header, which includes the table name,
 *          an Enable/Disable toggle button, and the close button.
 *
 * Fields:
 * - table: The selected manifest table entry.
 * - onToggleDisabled: Called when the Enable/Disable button is clicked.
 * - onClose: Called when the panel close (X) button is clicked.
 *
 * Sync: All data sourced from useLegacySchemaManifest via parent props.
 */
export interface TableDetailSlideInHeaderProps {
  table: SchemaManifestTableEntry;
  onToggleDisabled: (tableName: string) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Slide-in header — exported for use as GridPageShell panelHeader prop
// ---------------------------------------------------------------------------

/**
 * @component TableDetailSlideInHeader
 *
 * Purpose:
 * - Custom panel header for the Schema Manifest Editor slide-in.
 * - Shows the fully-qualified table name and an Enable / Disable action button.
 *
 * Props: See TableDetailSlideInHeaderProps.
 *
 * Behavior:
 * - Button label is "Disable" when the table is currently enabled, and "Enable" when disabled.
 * - Clicking the button calls onToggleDisabled immediately.
 *
 * Data Source: local (from useLegacySchemaManifest via parent props)
 *
 * Constraints: No API calls. State-free; all state owned by parent.
 *
 * @reusable
 * Usage: Pass as `panelHeader` prop to GridPageShell.
 * Rules: presentation-only; parent owns state.
 */
export function TableDetailSlideInHeader({ table, onToggleDisabled, onClose }: TableDetailSlideInHeaderProps) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 shrink-0">
      <div className="flex-1 min-w-0 mr-1">
        <h2 className="text-base font-semibold truncate text-gray-900">
          <span className="font-mono">{table.schema}.{table.table}</span>
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          {table.disabled ? 'Disabled — excluded from migration' : 'Enabled — included in migration'}
        </p>
      </div>
      <button
        onClick={() => onToggleDisabled(table.table)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
          table.disabled
            ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
        title={table.disabled ? 'Enable this table for migration' : 'Disable this table from migration'}
      >
        {table.disabled
          ? <><Eye className="h-3.5 w-3.5" />Enable</>
          : <><EyeOff className="h-3.5 w-3.5" />Disable</>}
      </button>
      <button
        onClick={onClose}
        className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500"
        title="Close panel"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slide-in tabs
// ---------------------------------------------------------------------------

type DetailTab = 'general' | 'columns' | 'foreignKeys' | 'relationships';

// ---------------------------------------------------------------------------
// Slide-in body
// ---------------------------------------------------------------------------

/**
 * @component TableDetailSlideIn
 *
 * Purpose:
 * - Four-tab detail body for a legacy schema manifest table row.
 * - Tabs: General, Columns, Foreign Keys, Relationships.
 *
 * Props: See TableDetailSlideInProps.
 *
 * Behavior:
 * - General: table-level metadata (schema, table name, status, column count, notes).
 * - Columns: all seeded columns with data type, nullable, identity flags; PK and FK badges.
 *   Each non-primary-key column includes a local-first enable/disable action.
 * - Foreign Keys: raw declared FK constraints touching this table (parent + child sides).
 * - Relationships: bidirectional view — parent-side rows where this table is the child,
 *   and child-side rows where this table is the parent.
 *
 * Data Source: local (Dexie via useLegacySchemaManifest, passed as props)
 *
 * Constraints: No API calls. No Dexie access. Pure presentation.
 *
 * @localFirst
 * Source of Truth: local (Dexie via useLegacySchemaManifest)
 * Write Flow: removeFk → parent hook → POST to server
 * Sync Strategy: server-authoritative; Dexie updated via system sync pull
 * Conflict Resolution: server wins
 */
export function TableDetailSlideIn({ table, fks, onRemoveFk, onToggleColumnDisabled }: TableDetailSlideInProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('general');
  const columns = table.columns ?? [];
  const disabledColumns = new Set(table.disabledColumns ?? []);

  // FK entries that touch this table, with their position in the global fks array
  const tableFkEntries = fks
    .map((fk, index) => ({ fk, index }))
    .filter(({ fk }) => fk.parentTable === table.table || fk.childTable === table.table);

  // Set of column names that are involved in any FK for this table (for badge rendering)
  const fkColumnSet = new Set(
    tableFkEntries.flatMap(({ fk }) => [
      fk.parentTable === table.table ? fk.parentColumn : '',
      fk.childTable === table.table ? fk.childColumn : '',
    ].filter(Boolean)),
  );

  // Bidirectional relationship views
  // Parent-side: this table is the CHILD (has an FK pointing up to a parent)
  const parentSideRels = tableFkEntries.filter(({ fk }) => fk.childTable === table.table);
  // Child-side: this table is the PARENT (other tables have FKs pointing to this one)
  const childSideRels = tableFkEntries.filter(({ fk }) => fk.parentTable === table.table);

  const tabs: { key: DetailTab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'columns', label: `Columns (${columns.length})` },
    { key: 'foreignKeys', label: `FKs (${tableFkEntries.length})` },
    { key: 'relationships', label: 'Relationships' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 px-4 shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap ${
              activeTab === tab.key
                ? 'border-orange-500 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-auto p-4">
        {/* General tab */}
        {activeTab === 'general' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-semibold text-gray-600 block mb-0.5">Schema</span>
                <span className="font-mono text-gray-900">{table.schema}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-600 block mb-0.5">Table</span>
                <span className="font-mono text-gray-900">{table.table}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-600 block mb-0.5">Status</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  table.disabled
                    ? 'bg-gray-100 text-gray-600 border border-gray-200'
                    : 'bg-green-100 text-green-700 border border-green-200'
                }`}>
                  {table.disabled ? 'Disabled' : 'Enabled'}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-600 block mb-0.5">Migration role</span>
                <span className="text-gray-700">{table.disabled ? 'Excluded' : 'Available'}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-600 block mb-0.5">Columns</span>
                <span className="text-gray-900">{columns.length}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-600 block mb-0.5">Relationships</span>
                <span className="text-gray-900">{tableFkEntries.length}</span>
              </div>
            </div>
            {table.notes && (
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs">
                <span className="font-semibold text-gray-600 block mb-1">Admin notes</span>
                <p className="text-gray-700 leading-relaxed">{table.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Columns tab */}
        {activeTab === 'columns' && (
          <div className="space-y-1">
            {columns.length === 0 && (
              <p className="text-xs text-gray-400 py-4 text-center">No columns found in the manifest.</p>
            )}
            {columns.map((col) => (
              <div
                key={col.column}
                className={`flex items-start justify-between rounded border px-2.5 py-2 text-xs ${disabledColumns.has(col.column) ? 'bg-gray-50 opacity-70' : ''}`}
                style={{ borderColor: 'rgba(0,0,0,0.07)' }}
              >
                <div className="flex items-center gap-1 flex-wrap min-w-0">
                  {col.isPrimaryKey && (
                    <span className="shrink-0 rounded bg-blue-50 border border-blue-200 px-1 py-px text-blue-700 font-semibold">PK</span>
                  )}
                  {fkColumnSet.has(col.column) && (
                    <span className="shrink-0 rounded bg-green-50 border border-green-200 px-1 py-px text-green-700 font-semibold">FK</span>
                  )}
                  {disabledColumns.has(col.column) && (
                    <span className="shrink-0 rounded bg-gray-100 border border-gray-200 px-1 py-px text-gray-600 font-semibold">OFF</span>
                  )}
                  <span className="font-mono text-gray-900 truncate">{col.column}</span>
                </div>
                <div className="ml-2 shrink-0 text-right text-gray-500 space-y-px">
                  <div>{col.dataType}{col.maxLength ? `(${col.maxLength})` : ''}</div>
                  {col.isNullable && <div className="text-gray-400">nullable</div>}
                  <button
                    onClick={() => onToggleColumnDisabled(table.table, col.column)}
                    disabled={col.isPrimaryKey}
                    className={`mt-1 inline-flex items-center rounded px-2 py-1 text-[10px] font-medium border transition-colors ${
                      col.isPrimaryKey
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                        : disabledColumns.has(col.column)
                          ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    title={col.isPrimaryKey ? 'Primary key columns cannot be disabled' : disabledColumns.has(col.column) ? 'Re-enable this column' : 'Disable this column from migration'}
                  >
                    {col.isPrimaryKey ? 'Locked' : disabledColumns.has(col.column) ? 'Enable' : 'Disable'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Foreign Keys tab */}
        {activeTab === 'foreignKeys' && (
          <div className="space-y-2">
            {tableFkEntries.length === 0 && (
              <p className="text-xs text-gray-400 py-4 text-center">
                No FK constraints declared for this table.
              </p>
            )}
            {tableFkEntries.map(({ fk, index }) => (
              <div
                key={`${fk.parentTable}.${fk.parentColumn}-${fk.childTable}.${fk.childColumn}-${index}`}
                className="rounded border px-3 py-2 text-xs"
                style={{ borderColor: 'rgba(0,0,0,0.08)' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 min-w-0">
                    {fk.constraintName && (
                      <div className="font-semibold text-gray-500 font-mono truncate">{fk.constraintName}</div>
                    )}
                    <div>
                      <span className="font-semibold text-blue-700">Parent: </span>
                      <span className="font-mono">{fk.parentSchema ?? 'dbo'}.{fk.parentTable}.</span>
                      <span className="font-mono font-semibold text-blue-800">{fk.parentColumn}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-green-700">Child: </span>
                      <span className="font-mono">{fk.childSchema ?? 'dbo'}.{fk.childTable}.</span>
                      <span className="font-mono font-semibold text-green-800">{fk.childColumn}</span>
                    </div>
                    {fk.label && <div className="text-gray-500 italic">{fk.label}</div>}
                  </div>
                  <button
                    onClick={() => onRemoveFk(index)}
                    className="shrink-0 p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove this FK declaration"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Relationships tab */}
        {activeTab === 'relationships' && (
          <div className="space-y-4">
            {tableFkEntries.length === 0 && (
              <p className="text-xs text-gray-400 py-4 text-center">
                No relationships declared for this table.
              </p>
            )}

            {/* Parent-side: this table is the child — has FKs pointing up */}
            {parentSideRels.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Parent references — this table points to
                </h4>
                <div className="space-y-1.5">
                  {parentSideRels.map(({ fk, index }) => (
                    <div
                      key={`parent-${index}`}
                      className="flex items-center gap-2 rounded border px-3 py-2 text-xs"
                      style={{ borderColor: 'rgba(37,99,235,0.15)', background: 'rgba(37,99,235,0.02)' }}
                    >
                      <span className="shrink-0 rounded bg-blue-50 border border-blue-200 px-1.5 py-px text-blue-600 font-semibold text-xs">↑</span>
                      <div className="min-w-0">
                        <span className="font-mono text-gray-700">{fk.childSchema ?? 'dbo'}.{fk.childTable}.</span>
                        <span className="font-mono font-semibold text-gray-900">{fk.childColumn}</span>
                        <span className="text-gray-400 mx-1.5">→</span>
                        <span className="font-mono text-blue-700">{fk.parentSchema ?? 'dbo'}.{fk.parentTable}.</span>
                        <span className="font-mono font-semibold text-blue-900">{fk.parentColumn}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Child-side: this table is the parent — other tables point to it */}
            {childSideRels.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Child references — tables pointing to this
                </h4>
                <div className="space-y-1.5">
                  {childSideRels.map(({ fk, index }) => (
                    <div
                      key={`child-${index}`}
                      className="flex items-center gap-2 rounded border px-3 py-2 text-xs"
                      style={{ borderColor: 'rgba(22,163,74,0.15)', background: 'rgba(22,163,74,0.02)' }}
                    >
                      <span className="shrink-0 rounded bg-green-50 border border-green-200 px-1.5 py-px text-green-600 font-semibold text-xs">↓</span>
                      <div className="min-w-0">
                        <span className="font-mono text-green-700">{fk.childSchema ?? 'dbo'}.{fk.childTable}.</span>
                        <span className="font-mono font-semibold text-green-900">{fk.childColumn}</span>
                        <span className="text-gray-400 mx-1.5">→</span>
                        <span className="font-mono text-gray-700">{fk.parentSchema ?? 'dbo'}.{fk.parentTable}.</span>
                        <span className="font-mono font-semibold text-gray-900">{fk.parentColumn}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
